import { css, type Theme } from '@emotion/react';
import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import { PushPin } from '@phosphor-icons/react';
import { UserPreferenceType } from 'common/CommonConstants';
import { trpc, useMutation } from 'client/lib/trpc';
import { useUser } from 'client/lib/User';
import { Button } from 'client/components/Button';
import { DropdownMenu, DropdownMenuItem, type DropdownMenuItemProps } from 'client/components/DropdownMenu';
import { useDevelopBlocker } from './useDevelopBlocker';

type DevelopMenuItemProps = DropdownMenuItemProps & { 'data-name'?: string };

export const DevelopMenuItem = ({ ...item }: DevelopMenuItemProps) => {
  const { user, updateSession } = useUser();

  const startingWarning = `This deployment is still starting up. ${item['data-name'] || 'Editor'} is not ready yet, please wait.`;
  const stoppedWarning = `This deployment is stopped. ${item['data-name'] || 'Editor'} cannot be opened. Please start the deployment first.`;

  const { menuItems, isWarningDisplayed, isErrorDisplayed } = useDevelopBlocker({ target: item.id, startingWarning, stoppedWarning });

  const pinMutation = useMutation(trpc.user.preferenceSave, {
    onSuccess: () => {
      updateSession();
    },
  });

  const pinnedEditorIDs = user?.pinnedEditorIDs || ['browser'];
  const isPinned = pinnedEditorIDs.includes(item.id);

  const handlePin = () => {
    if (pinnedEditorIDs.includes(item.id)) {
      pinMutation.mutate({
        userPreferenceTypeID: UserPreferenceType.EDITORS,
        pinnedEditorIDs: pinnedEditorIDs.filter((id) => id !== item.id),
      });
    } else {
      pinMutation.mutate({ userPreferenceTypeID: UserPreferenceType.EDITORS, pinnedEditorIDs: [...pinnedEditorIDs, item.id] });
    }
  };

  let itemElement = <DropdownMenuItem {...item} />;

  if (isWarningDisplayed || isErrorDisplayed) {
    itemElement = (
      <RadixDropdownMenu.Sub>
        <DropdownMenuItem {...item} href={undefined} onClick={undefined} component={<RadixDropdownMenu.SubTrigger />} />
        <RadixDropdownMenu.Portal>
          <DropdownMenu.Content items={menuItems} side="left" sideOffset={46 + 24} component={<RadixDropdownMenu.SubContent />} />
        </RadixDropdownMenu.Portal>
      </RadixDropdownMenu.Sub>
    );
  }

  return (
    <li css={itemStyle}>
      {itemElement}
      <Button primary={isPinned} loading={pinMutation.isLoading} onClick={handlePin}>
        <PushPin weight={isPinned ? 'fill' : 'regular'} />
      </Button>
    </li>
  );
};

const itemStyle = (theme: Theme) => css`
  display: flex;
  gap: 8px;
  list-style: none;

  > a,
  > div {
    flex: 1;
  }

  > .button {
    border-radius: ${theme.borderRadius}px;
    /* border-top-left-radius: 0;
    border-bottom-left-radius: 0; */

    :not(:hover) {
      /* background-color: transparent; */
    }

    > span {
      margin-left: 0;
      margin-right: 0;
    }
  }
`;
